import ModalDrawer from "components/source/shared/modal-drawer/modal-drawer";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { displayModal } from "actions/shared-actions";
import { MODAL_NAMES } from "constants/modal-names";
import { navigateTo } from "helpers/location-helpers";
import Routes from "routes";
import CopyPromoIcon from "images/acquisition/copy.svg";
import ClothingRack from "images/acquisition/plans-drawer/clothing-rack.svg";
import SwapHanger from "images/acquisition/plans-drawer/swap-hanger.svg";
import Shipment from "images/acquisition/plans-drawer/shipment.svg";
import { useSubscriptionAtbAnalytics } from "../../hoc/with-subscription-atb-analytics";
import { useEffect, useState } from "react";
import { flagsAndExperimentNames } from "components/source/hoc/with-flags-and-experiments";

export default function PlansDrawerModal({ isOpen }) {
  const promoCode = "GETDRESSED";
  const twoSwapTierId = "2";
  const dispatch = useDispatch();
  const displayedModal = useSelector(state => state.displayedModal);
  const flagsAndExperiments = useSelector(state => state.flagsAndExperiments);
  const [shouldShowCopyPromoSuccess, setShouldShowCopyPromoSuccess] = useState(false);

  const isPlansDrawerPriceChangeEnabled =
    flagsAndExperiments?.[flagsAndExperimentNames.PROSPECTS_SF_IOS_PLANS_DRAWER_PRICING_UPDATE];

  const [fireViewItemAnalytics, fireAtbAnalytics] = useSubscriptionAtbAnalytics();

  useEffect(() => {
    if (displayedModal === MODAL_NAMES.PLANS_DRAWER) {
      fireViewItemAnalytics();
    }
  }, [displayedModal]);

  const renderHeader = () => {
    const onCopyPromoClick = () => {
      navigator.clipboard.writeText(promoCode);

      const copiedPromo = navigator.clipboard.readText();

      if (copiedPromo) {
        setShouldShowCopyPromoSuccess(true);
        setTimeout(() => setShouldShowCopyPromoSuccess(false), 3000);
      }
    };

    return (
      <div className="flex flex-col items-center min-h-20 md:min-h-24 gap-0.5 md:gap-1">
        <div className="h-11 md:h-16 flex flex-col items-center gap-2 md:gap-4">
          <span className="text-xl md:text-h3 font-sans md:font-serif">{`Get ${
            isPlansDrawerPriceChangeEnabled ? "$50" : "$45"
          } Off Your First Month`}</span>
          <p className="flex flex-row items-center">
            <span className="text-darkGray text-sm md:text-md"> Sign up with code:&nbsp;</span>{" "}
            <span className="font-semibold pr-1 text-sm md:text-md ">{`${promoCode}`}</span>
            <CopyPromoIcon className="mx-2" onClick={onCopyPromoClick} data-test-id="copy-promo" />
          </p>
        </div>
        {shouldShowCopyPromoSuccess && <span className="text-tealGreen text-sm">Copied!</span>}
      </div>
    );
  };

  const renderPlanTiles = () => {
    const boxBaseClass = "flex flex-col grow items-center text-white bg-wine h-20 flex-1 p-3";

    return (
      <div className="px-8 w-full max-w-md">
        <div className="pt-6 md:pt-7 flex flex-row gap-px items-center w-full max-w-md">
          <div className={`${boxBaseClass} rounded-tl-lg rounded-bl-lg mr-px`}>
            <span className="text-h2 font-serif">2</span>
            <span className="text-lightGray text-sm text-center">shipments / mo</span>
          </div>
          <div className={`${boxBaseClass}`}>
            <span className="text-h2 font-serif">5</span>
            <span className="text-lightGray text-sm text-center">items / shipment</span>
          </div>
          <div className={`${boxBaseClass} rounded-tr-lg rounded-br-lg ml-px`}>
            <div className="flex flex-row gap-0.5 items-center">
              <span className="text-lg">$</span>
              <span className="text-h2 font-serif">{isPlansDrawerPriceChangeEnabled ? "114" : "99"}</span>
              <span className="line-through">{isPlansDrawerPriceChangeEnabled ? "$164" : "$144"}</span>
            </div>
            <span className="text-lightGray text-sm text-center">first month</span>
          </div>
        </div>
      </div>
    );
  };

  const renderSubscriptionDetails = () => {
    const detailBaseClass = "flex flex-row items-center";

    const chooseFiveCopy = {
      header: "Choose 5 Items",
      body: "Explore thousands of styles and 750+ designers each month to create your shipment.",
    };

    const wearThemCopy = {
      header: "Wear Them Out",
      body: "With personal style tips and free replacements during your first 60 days, you can wear with confidence.",
    };

    const swapOrKeepCopy = {
      header: "Swap or Keep in Your Next Shipment",
      body: "Each shipment you can swap items for new styles, with the option to buy or keep renting for as long as you want.",
    };

    const renderDetailCopy = copy => {
      return (
        <div className="flex flex-col items-start gap-1 ml-4 md:ml-6">
          <div className="text-md md:text-lg">{`${copy.header}`}</div>
          <div className="text-sm md:text-md text-darkGray font-normal">{`${copy.body}`}</div>
        </div>
      );
    };

    return (
      <div className={"flex flex-col mx-6 pb-12 gap-6 md:mx-8 md:mt-10 md:pb-24 md:gap-12"}>
        <div className={`${detailBaseClass}`}>
          <ClothingRack />
          {renderDetailCopy(chooseFiveCopy)}
        </div>
        <div className={`${detailBaseClass}`}>
          <SwapHanger />
          {renderDetailCopy(wearThemCopy)}
        </div>
        <div className={`${detailBaseClass}`}>
          <Shipment />
          {renderDetailCopy(swapOrKeepCopy)}
        </div>
      </div>
    );
  };

  const closePlansDrawerModal = () => {
    if (displayedModal === MODAL_NAMES.PLANS_DRAWER) {
      return dispatch(displayModal(false));
    }
  };

  const renderExplorePlansLink = () => {
    return (
      <a className="underline font-medium pt-3" href={Routes.LP.plans}>
        Explore All Plans
      </a>
    );
  };

  const renderSignUpButton = () => {
    const onClickSignUp = () => {
      fireAtbAnalytics(twoSwapTierId);
      navigateTo(`/ce-checkout/membershipTierId/${twoSwapTierId}`);
    };

    return (
      <div className="flex flex-row justify-center sticky bottom-0">
        <button
          className={"bg-wine w-96 h-11 rounded-full text-white mb-6 md:my-6 mx-8"}
          onClick={onClickSignUp}
          data-test-id="sign-up-button">
          Sign Up
        </button>
      </div>
    );
  };

  return (
    <ModalDrawer isOpen={isOpen} peekHeight={"70vh"} handleClose={closePlansDrawerModal} desktopWidth="34%">
      <div className={"mt-14 md:mt-20 flex flex-col items-center"}>
        {renderHeader()}
        {renderPlanTiles()}
        {renderExplorePlansLink()}
      </div>
      <hr className={"border-t border-lighterGray my-6 md:my-12 mx-6"}></hr>
      {renderSubscriptionDetails()}
      {renderSignUpButton()}
    </ModalDrawer>
  );
}

PlansDrawerModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
};
